/**
 * @module common/components/project_tile
 */
import React, { Component, PropTypes } from 'react';
import classNames from 'classnames';
import { v4 } from 'node-uuid';
import truncate from 'lodash/truncate';
import { getSubCategory } from 'project/ducks/project';
import Menu from 'explore/containers/project_menu';
import { getTimeLeftLabel, cleanProjectTitle, isMobile } from 'common/utils';
import Feedback from 'common/containers/project_feedback';
import ProjectLink from 'common/containers/project_link';
import ProjectTags from 'common/components/project_tags';
import Thumbnail from 'common/components/thumbnail';
import FilterTag from 'common/filter/containers/tag';
import Tooltip from 'react-tooltip';

/**
 * Renders a reusable list element to display some entry.
 *
 * @author Kuba Siemiątkowski <kuba@crowdspring.com>
 * @author Jing Wang <jing@crowdspring.com>
 */
export default class ProjectTile extends Component {
  // Ordering of project stats based on user type
  renderAward(a) {
    return (<span className="amount">
      <span className="dollar-sign">$</span>{a}
    </span>);
  }

  renderResultStats() {
    const { item, append, userType, showTooltips, linkEntriesStats } = this.props;
    let statsList = null;
    const daysLeft = getTimeLeftLabel(item.deadline);

    if (userType === 'creative') {
      statsList = [
        { label: 'Award', amt: item.total_awards_and_tips },
        { label: 'Time Left', amt: daysLeft },
        { label: 'Feedback' },
        { label: 'Entries', amt: item.entry_count },
      ];
    } else {
      statsList = [
        { label: 'Award', amt: item.total_awards_and_tips },
        { label: 'Entries', amt: item.entry_count },
        item.status === 'open'
          ? { label: 'Time Left', amt: daysLeft }
          : { label: 'Creatives', amt: item.creative_count },
        { label: 'Feedback' },
      ];
    }

    return (
      <div className="small-16 column">
        <ul className="project-stats">
          { statsList.map(stat => {
            const feedbackTooltipId = (
              !!showTooltips && stat.label === 'Feedback'? `tooltip-feedback-${item.id}` : null
            );

            return (
              <li key={v4()} className={stat.label.toLowerCase().replace(/\s/, '-')}
                data-tip={!!feedbackTooltipId} data-for={feedbackTooltipId}
                data-event={isMobile()? "click" : undefined}>
                { ((stat.label === 'Award') && item.assured)
                  ? <span className="stat-label text-success">Assured</span>
                  : <span className="stat-label">{stat.label}</span>
                }
                { stat.label === 'Feedback' && <Feedback project={item} /> }

                { stat.label !== 'Feedback' && (stat.label !== 'Entries' || !linkEntriesStats) &&
                <span className="stat-value">
                  {(stat.label === 'Award' || stat.label === 'Price')
                    ? this.renderAward(stat.amt)
                    : stat.amt}
                </span> }

                { stat.label === 'Entries' && linkEntriesStats &&
                <span className="stat-value">
                  <ProjectLink project={item} append={append}>{ stat.amt || '0' }</ProjectLink>
                </span> }

                { !!feedbackTooltipId &&
                <Tooltip class="tooltip" effect="solid" place="bottom" id={feedbackTooltipId}>
                  To help creatives pick projects, we tell them whether the client has been actively scoring and commenting on submitted entries. A smiley face means the client has been very active, a neutral face means the client has been somewhat active, and a frowny face means the client has not been active. The best and most successful clients always have a smiley face in their project.
                </Tooltip> }
              </li>
            );
          }) }
        </ul>
      </div>
    );
  }

  renderSubcatTag() {
    const { item, categories, subcategoryFilter } = this.props;
    const subcat = getSubCategory(item, categories);

    if (!subcat || !subcat.id || !subcategoryFilter) {
      return (
        <span className="tag tag-plain tag-sm">{ subcat && subcat.name? subcat.name : '??' }</span>
      );
    }

    return (
      <FilterTag field="sub_category" resourceName={subcategoryFilter.resourceName}
        tagValue={subcat.id.toString()} tagLabel={subcat.name} />
    );
  }

  renderTitleContent() {
    const { item, append } = this.props;
    const title = item.title? truncate(
      item.is_title_cleanable? cleanProjectTitle(item.title) : item.title, { length: 60 }
    ) : '';

    return (
      <span>
        <ProjectLink
          project={item}
          alt={item.title}
          linkTitle={title}
          append={append}
        />
        { item.status === 'draft' && <em> (draft)</em> }
      </span>
    );
  }

  render() {
    const { item, authenticated, append, titleTag, industries, showTooltips } = this.props;
    const isBlind = item.private_gallery;
    const isNDA = item.nda_required;
    const isNaming = item.entry_type === 'name';
    const isNew = !item.project_thumbnail;
    const isPlatinum = item.platinum_only;
    const isElite = item.elite_only;

    const itemClass = classNames('tile', 'project-tile', {
      featured: item.featured,
      assured: item.assured,
      anonymous: !authenticated,
      naming: isNaming,
      ndad: isNDA,
      blind: isBlind,
      platinum: isPlatinum,
      elite: isElite
    });

    const thumbnailClass = classNames('thumbnail flex', {
      ndad: isNDA,
      blind: isBlind,
      new: isNew
    });

    const thumbnailImage = (
      !isNaming && !isNDA && !isBlind && !isNew ? item.project_thumbnail : null
    );

    const industry = (industries || []).find(i => i.slug === item.industry);
    const resultsClasses =
      'small-16 medium-10 medium-offset-6 medium-order-3 large-11 large-offset-5 columns';

    return (
      <div className="row project" key={v4()}>
        <div className="small-16 column">
          <div className={itemClass}>
            <div className="row">
              <div className="tile-tools">
                <Menu project={item} />
              </div>
              <div className="small-16 medium-10 medium-order-2 large-11 columns">
                <div className="expanded row">
                  <div className="columns small-16">
                    <div className="common-project-info">
                      <div className="project-meta">
                        <div className="project-categories">
                          { this.renderSubcatTag() }
                          <span className="dot" />
                          <span className="tag tag-plain tag-sm">
                            {industry ? industry.name : ''}
                          </span>
                        </div>
                        { titleTag === "h3"?
                        <h3 className="project-title">
                          { this.renderTitleContent() }
                        </h3> :
                        <h2 className="project-title">
                          { this.renderTitleContent() }
                        </h2> }
                      </div>
                      <ProjectTags project={item} showTooltips={showTooltips} className="project-tags" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="small-16 medium-6 medium-order-1 large-5 columns project-thumbnail">
                <ProjectLink
                  project={item}
                  append={append}
                >
                  <Thumbnail image={thumbnailImage} className={thumbnailClass}>
                    { (isNDA || isBlind || isNew) &&
                    <div className="thumbnail-special-content text-center">
                      { isNDA &&
                      <div><i className="fa fa-lock" /><br />Private</div> }
                      { isBlind && !isNDA &&
                      <div><i className="fa fa-eye-slash" /><br />Blind</div> }
                      { isNew && !isNDA && !isBlind &&
                      <div>New Project</div> }
                    </div> }
                  </Thumbnail>
                </ProjectLink>
              </div>
              <div className={resultsClasses}>
                <div className="expanded row">
                  { this.renderResultStats() }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

ProjectTile.propTypes = {
  item: PropTypes.object,
  categories: PropTypes.array,
  authenticated: PropTypes.bool,
  userType: PropTypes.string,
  industries: PropTypes.array,
  append: PropTypes.string,
  titleTag: PropTypes.string,
  subcategoryFilter: PropTypes.object,
  showTooltips: PropTypes.bool,
  linkEntriesStats: PropTypes.bool,
};



// WEBPACK FOOTER //
// ./common/components/project_tile.jsx